
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Language } from '../types';
import { TRANSLATIONS, DISASTER_PROTOCOLS, EMERGENCY_NUMBERS } from '../constants';

interface SOSOverlayProps {
  alert: Alert;
  onDismiss: () => void;
  lang: Language;
}

const SOSOverlay: React.FC<SOSOverlayProps> = ({ alert, onDismiss, lang }) => {
  const t = TRANSLATIONS[lang];
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [numbers, setNumbers] = useState(EMERGENCY_NUMBERS['DEFAULT']);

  // Protocol
  const protocol = DISASTER_PROTOCOLS[alert.type];

  useEffect(() => {
    // Attempt to determine country from alert location string or assume default
    // In a real app, use reverse geocoding country code (ISO)
    // Here we stick to default/generic for safety or US/UK if detectable
    if (alert.location.includes("UK") || alert.location.includes("London")) setNumbers(EMERGENCY_NUMBERS['GB']);
    else if (alert.location.includes("USA") || alert.location.includes("NY")) setNumbers(EMERGENCY_NUMBERS['US']);
    else if (alert.location.includes("UAE") || alert.location.includes("Dubai")) setNumbers(EMERGENCY_NUMBERS['AE']);
    else setNumbers(EMERGENCY_NUMBERS['DEFAULT']);

    // Play Siren
    const audio = new Audio("https://cdn.pixabay.com/audio/2021/08/04/audio_0625c1539c.mp3"); // Standard Alarm
    audio.loop = true;
    audio.volume = 1.0;
    audio.play().catch(e => console.warn("Audio autoplay blocked", e));
    audioRef.current = audio;

    // Vibrate
    if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 1000]);

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [alert]);

  return (
    <div className="fixed inset-0 z-[200] bg-danger text-white flex flex-col p-6 animate-fade-in overflow-y-auto">
      
      {/* Flashing Background */}
      <div className="absolute inset-0 bg-red-900 animate-pulse pointer-events-none"></div>
      
      <div className="relative z-10 flex flex-col h-full">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center animate-spin-slow">
                      <span className="text-2xl">🚨</span>
                  </div>
                  <div>
                      <h1 className="text-3xl font-bold tracking-tighter uppercase leading-none">{t.emergency}</h1>
                      <p className="text-xs font-mono bg-black/20 px-2 py-0.5 rounded mt-1 inline-block">{t.criticalAlert}</p>
                  </div>
              </div>
          </div>

          {/* Main Alert Info */}
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border-2 border-white/20 mb-6 shadow-2xl">
              <h2 className="text-2xl font-bold uppercase mb-2 text-yellow-300">{alert.type} DETECTED</h2>
              <p className="text-sm font-mono opacity-90 mb-4">{alert.location} • {alert.distance}</p>
              
              <div className="h-1 w-full bg-white/20 mb-4"></div>
              
              <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white/70">IMMEDIATE ACTION REQUIRED:</h3>
                  {protocol ? (
                      <ul className="space-y-2">
                          {protocol.steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-3">
                                  <span className="w-6 h-6 rounded-full bg-white text-danger font-bold flex items-center justify-center text-xs shrink-0">{i+1}</span>
                                  <span className="text-lg font-bold leading-tight">{step}</span>
                              </li>
                          ))}
                      </ul>
                  ) : (
                      <p className="text-lg font-bold">SEEK IMMEDIATE SHELTER. FOLLOW LOCAL AUTHORITY INSTRUCTIONS.</p>
                  )}
              </div>
          </div>

          {/* Quick Dial Buttons */}
          <div className="grid grid-cols-3 gap-3 mb-6">
              <a href={`tel:${numbers.police}`} className="bg-blue-600/90 hover:bg-blue-500 rounded-xl p-4 flex flex-col items-center justify-center gap-2 border border-white/20 active:scale-95 transition-transform">
                  <span className="text-2xl">👮</span>
                  <span className="text-xs font-bold">{t.callPolice}</span>
                  <span className="text-lg font-mono font-bold">{numbers.police}</span>
              </a>
              <a href={`tel:${numbers.ambulance}`} className="bg-green-600/90 hover:bg-green-500 rounded-xl p-4 flex flex-col items-center justify-center gap-2 border border-white/20 active:scale-95 transition-transform">
                  <span className="text-2xl">🚑</span>
                  <span className="text-xs font-bold">{t.callMedic}</span>
                  <span className="text-lg font-mono font-bold">{numbers.ambulance}</span>
              </a>
              <a href={`tel:${numbers.fire}`} className="bg-orange-600/90 hover:bg-orange-500 rounded-xl p-4 flex flex-col items-center justify-center gap-2 border border-white/20 active:scale-95 transition-transform">
                  <span className="text-2xl">🚒</span>
                  <span className="text-xs font-bold">{t.callFire}</span>
                  <span className="text-lg font-mono font-bold">{numbers.fire}</span>
              </a>
          </div>

          <div className="mt-auto">
              <button 
                onClick={onDismiss}
                className="w-full py-5 bg-white text-danger font-black text-xl rounded-xl shadow-[0_0_50px_rgba(255,255,255,0.5)] active:scale-95 transition-transform tracking-widest"
              >
                  {t.iAmSafe}
              </button>
              <p className="text-center text-[10px] opacity-60 mt-4 font-mono">DISMISSING THIS SCREEN WILL STOP THE ALARM</p>
          </div>

      </div>
    </div>
  );
};

export default SOSOverlay;
